import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Radio, Bot, Plug, Users, UsersRound, Eye, Waypoints } from 'lucide-react';
import { CardGrid } from '@reflex/ui/components/shared/CardGrid';
import { StatCard } from '@reflex/ui/components/shared/StatCard';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { timeAgo } from '@reflex/ui/lib/format';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { AgentStatusDot } from '@/components/status/agent/AgentStatusChip';
import { StreamFlowCell } from '@/components/monitor/StreamFlowCell';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { useUsers } from '@/hooks/auth/useUsers';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { ROUTES } from '@/lib/routes';
import { getUserDisplayName } from '@/lib/agent-utils';
import type { UsageSnapshot, UsageStream, UsageTeamTotals, UsageUserTotals } from '@reflex/shared';
import type { User } from '@/lib/types';

/**
 * Synthetic per-team row key for users who belong to no team. Rendered as a
 * "No team" bucket so the team table always accounts for every user row.
 */
const NO_TEAM = '__no_team__';

interface AdminOrgUsageDetailProps {
  organizationId: string;
  snapshot: UsageSnapshot | null;
}

/**
 * Super-admin drill-down for one organization's live usage. Breaks the org's
 * running agents, Axon streams/subscriptions/viewers, and WebSocket
 * connections down by team and by user, so admins can see who is consuming
 * resources. Fed by the same live usage snapshot as the monitor page.
 */
export function AdminOrgUsageDetail({ organizationId, snapshot }: AdminOrgUsageDetailProps) {
  const { data: users } = useUsers();
  const orgRoutes = useOrgRoutes();

  const userMap = useMemo(() => {
    const map = new Map<string, User>();
    for (const u of users ?? []) map.set(u.id, u);
    return map;
  }, [users]);

  const org = snapshot?.perOrg.find((o) => o.organizationId === organizationId) ?? null;

  const perUser = useMemo(
    () => (snapshot?.perUser ?? []).filter((r) => r.organizationId === organizationId),
    [snapshot, organizationId],
  );

  const streams = useMemo(
    () => (snapshot?.streams ?? []).filter((s) => s.organizationId === organizationId),
    [snapshot, organizationId],
  );

  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const team of snapshot?.perTeam ?? []) map.set(team.teamId, team.name);
    return map;
  }, [snapshot]);

  // The org's team rows plus a synthetic "No team" bucket aggregating the
  // per-user rows that belong to no team (including the anonymous bucket).
  const teamRows = useMemo<UsageTeamTotals[]>(() => {
    const rows = (snapshot?.perTeam ?? []).filter((t) => t.organizationId === organizationId);
    const unassigned = perUser.filter((r) => r.teamIds.length === 0);
    if (unassigned.length > 0) {
      const noTeam: UsageTeamTotals = {
        teamId: NO_TEAM,
        organizationId,
        name: 'No team',
        slug: null,
        memberCount: unassigned.filter((r) => r.userId !== null).length,
        runningAgents: 0,
        activeStreams: 0,
        connectedSockets: 0,
        axonViewers: 0,
      };
      for (const row of unassigned) {
        noTeam.runningAgents += row.runningAgents;
        noTeam.activeStreams += row.activeStreams;
        noTeam.connectedSockets += row.connectedSockets;
        noTeam.axonViewers += row.axonViewers;
      }
      return [...rows, noTeam];
    }
    return rows;
  }, [snapshot, organizationId, perUser]);

  const teamColumns: DataTableColumn<UsageTeamTotals>[] = useMemo(
    () => [
      {
        header: 'Team',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (row) => (
          <span className="flex items-center gap-2 text-sm">
            <UsersRound className="icon-sm shrink-0 text-muted-foreground" />
            <span className={row.teamId === NO_TEAM ? 'text-muted-foreground' : 'font-medium'}>
              {row.name}
            </span>
          </span>
        ),
      },
      {
        header: 'Members',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums text-muted-foreground',
        cell: (row) => row.memberCount,
      },
      {
        header: 'Running agents',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.runningAgents,
      },
      {
        header: 'Active streams',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.activeStreams,
      },
      {
        header: 'Sockets',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.connectedSockets,
      },
      {
        header: 'Axon viewers',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.axonViewers,
      },
    ],
    [],
  );

  const userColumns: DataTableColumn<UsageUserTotals>[] = useMemo(
    () => [
      {
        header: 'User',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (row) => {
          const displayName = row.name ?? row.login ?? 'Unknown';
          return row.userId ? (
            <Link
              to={ROUTES.userProfile(row.userId)}
              className="flex items-center gap-2 hover:underline"
            >
              <UserAvatar avatarUrl={row.avatarUrl ?? undefined} userName={displayName} size="xs" />
              <span className="text-sm">{displayName}</span>
            </Link>
          ) : (
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <UserAvatar userName={displayName} size="xs" />
              {displayName}
            </span>
          );
        },
      },
      {
        header: 'Teams',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5 text-xs text-muted-foreground',
        cell: (row) =>
          row.teamIds.length > 0
            ? row.teamIds.map((id) => teamNameById.get(id) ?? id).join(', ')
            : '--',
      },
      {
        header: 'Running agents',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.runningAgents,
      },
      {
        header: 'Active streams',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.activeStreams,
      },
      {
        header: 'Sockets',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.connectedSockets,
      },
      {
        header: 'Axon viewers',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.axonViewers,
      },
    ],
    [teamNameById],
  );

  const streamColumns: DataTableColumn<UsageStream>[] = useMemo(
    () => [
      {
        header: 'Stream',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (stream) => (
          <span className="font-mono text-xs text-muted-foreground">
            {stream.streamId.length > 12
              ? `${stream.streamId.slice(0, 8)}…${stream.streamId.slice(-4)}`
              : stream.streamId}
          </span>
        ),
      },
      {
        header: 'Agent',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (stream) =>
          stream.agentId ? (
            <Link
              to={orgRoutes.agent(stream.agentId)}
              className="flex items-center gap-2 hover:underline"
            >
              {stream.agentStatus && <AgentStatusDot status={stream.agentStatus} />}
              <span className="max-w-[220px] truncate font-medium">
                {stream.agentName ?? stream.agentId}
              </span>
            </Link>
          ) : stream.flowName ? (
            <StreamFlowCell flowName={stream.flowName} flowRunId={stream.flowRunId} />
          ) : (
            <span className="text-xs text-muted-foreground">automation / no agent</span>
          ),
      },
      {
        header: 'Owner',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (stream) => {
          const user = stream.ownerUserId ? userMap.get(stream.ownerUserId) : null;
          return user ? (
            <Link
              to={ROUTES.userProfile(user.id)}
              className="flex items-center gap-1.5 hover:underline"
            >
              <UserAvatar
                avatarUrl={user.avatarUrl ?? undefined}
                userName={getUserDisplayName(user)}
                size="xs"
              />
              <span className="text-xs text-muted-foreground">{getUserDisplayName(user)}</span>
            </Link>
          ) : (
            <span className="text-xs text-muted-foreground">--</span>
          );
        },
      },
      {
        header: 'Subs',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (stream) => stream.subscribers,
      },
      {
        header: 'Viewers',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (stream) => stream.viewers,
      },
      {
        header: 'Last event',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right',
        cell: (stream) => (
          <span className="text-xs text-muted-foreground">
            {stream.lastEventAt ? timeAgo(stream.lastEventAt) : '--'}
          </span>
        ),
      },
    ],
    [userMap, orgRoutes],
  );

  const waiting = (
    <EmptyState
      icon={<Radio className="h-5 w-5 text-muted-foreground" />}
      title="Waiting for first usage snapshot…"
      className="py-10"
    />
  );

  return (
    <div className="space-y-4" data-testid="admin-org-usage-detail">
      <CardGrid cols={3}>
        <StatCard
          label="Running Agents"
          value={org?.runningAgents ?? '—'}
          icon={<Bot className="h-5 w-5" />}
          description="Agents in starting/running/needs_input"
        />
        <StatCard
          label="Active Streams"
          value={org?.activeStreams ?? '—'}
          icon={<Radio className="h-5 w-5" />}
          description={
            org && org.agentlessStreams > 0
              ? `${org.agentlessStreams} agentless (automation runs)`
              : 'Open Axon streams'
          }
        />
        <StatCard
          label="WebSocket Connections"
          value={org?.connectedSockets ?? '—'}
          icon={<Plug className="h-5 w-5" />}
          description="Live sockets from this org's users"
        />
        <StatCard
          label="Axon Subscriptions"
          value={org?.axonSubscriptions ?? '—'}
          icon={<Waypoints className="h-5 w-5" />}
          description="In-process subscribers across the org's streams"
        />
        <StatCard
          label="Axon Viewers"
          value={org?.axonViewers ?? '—'}
          icon={<Eye className="h-5 w-5" />}
          description="Browser viewers watching the org's streams"
        />
      </CardGrid>

      <SectionCard
        title="Usage by Team"
        icon={<UsersRound />}
        description="Team totals fold in each member's usage. Users in more than one team count toward each, so team rows can sum to more than the org total."
        headerClassName="pb-3"
        contentClassName="px-0 pb-0"
      >
        {!snapshot ? (
          waiting
        ) : teamRows.length === 0 ? (
          <EmptyState
            icon={<UsersRound className="h-5 w-5 text-muted-foreground" />}
            title="No teams or usage to report"
            className="py-10"
          />
        ) : (
          <DataTable<UsageTeamTotals>
            data={teamRows}
            columns={teamColumns}
            rowKey={(row) => row.teamId}
          />
        )}
      </SectionCard>

      <SectionCard
        title="Usage by User"
        icon={<Users />}
        description="Running agents, streams, sockets, and open stream viewers by user."
        headerClassName="pb-3"
        contentClassName="px-0 pb-0"
      >
        {!snapshot ? (
          waiting
        ) : perUser.length === 0 ? (
          <EmptyState
            icon={<Users className="h-5 w-5 text-muted-foreground" />}
            title="No usage to report"
            className="py-10"
          />
        ) : (
          <DataTable<UsageUserTotals>
            data={perUser}
            columns={userColumns}
            rowKey={(row) => row.userId ?? 'unknown'}
          />
        )}
      </SectionCard>

      <SectionCard
        title="Active Streams"
        icon={<Radio />}
        description="Live Axon streams owned by this organization, updated every few seconds."
        headerClassName="pb-3"
        contentClassName="px-0 pb-0"
      >
        {!snapshot ? (
          waiting
        ) : streams.length === 0 ? (
          <EmptyState
            icon={<Radio className="h-5 w-5 text-muted-foreground" />}
            title="No active streams"
            className="py-10"
          />
        ) : (
          <DataTable<UsageStream>
            data={streams}
            columns={streamColumns}
            rowKey={(stream) => stream.streamId}
          />
        )}
      </SectionCard>
    </div>
  );
}
